import useGetApi from 'src/api/useGetApi';
import useMutationApi from 'src/api/useMutationApi';

export interface ProductGroupMetadata {
  enableProductCodes?: string[];
}

export interface ProductGroupTag {
  id: string;
  name: string | null;
  description: string | null;
}

export interface ProductGroup {
  id: string;
  name: string;
  description: string | null;
  supplierId: string;
  isHistorical: boolean;
  isCatchAll: boolean;
  metadata: ProductGroupMetadata | null;
  supplier?: {
    id: string;
    name: string;
  };
  tags?: ProductGroupTag[];
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateProductGroupRequest {
  supplierId: string;
  name: string;
  description?: string;
  isHistorical?: boolean;
}

export interface UpdateProductGroupRequest {
  name: string;
  description: string;
  isHistorical?: boolean;
}

// Deletion impact response types
export interface DeletionImpactInvoice {
  id: string;
  invoiceNumber: string;
  totalAmount: number;
  lineItemCount: number;
  affectedLineItemCount: number;
  lineItemDateRange: {
    earliest: string;
    latest: string;
  } | null;
}

export interface DeletionImpactRule {
  id: string;
  name: string;
  agreementId: string;
  agreementName: string;
  usageType: 'qualifying' | 'affected' | 'both';
  inclusionType: {
    qualifying: string | null;
    affected: string | null;
  };
  impact: 'will_become_invalid' | 'behavior_change' | 'minor';
  impactReason: string;
}

export interface ProductGroupDeletionImpact {
  productGroup: {
    id: string;
    name: string;
  };
  invoices: DeletionImpactInvoice[];
  rules: DeletionImpactRule[];
  summary: {
    invoiceCount: number;
    totalLineItemsToDelete: number;
    ruleCount: number;
    agreementCount: number;
    lineItemDateRange: {
      earliest: string;
      latest: string;
    } | null;
  };
  canDeleteWithoutInvoices: boolean;
}

export interface DeleteProductGroupResponse {
  message: string;
  deletedCounts: {
    csvMigrations: number;
    rebateLineItems: number;
    invoiceLineItems: number;
    invoiceAssociations: number;
    invoices: number;
    qualifiers: number;
    affectees: number;
  };
}

// Get all product groups for a supplier
export const useProductGroupsBySupplier = (
  supplierId: string,
  enabled = true,
) => {
  return useGetApi<ProductGroup[]>({
    endpoint: `rebates/product-groups/supplier/${supplierId}`,
    options: {
      enabled: enabled && !!supplierId,
    },
  });
};

// Get all product groups for a supplier with tags included
export const useProductGroupsBySupplierWithTags = (
  supplierId: string,
  enabled = true,
) => {
  return useGetApi<ProductGroup[]>({
    endpoint: `rebates/product-groups/supplier/${supplierId}/with-tags`,
    options: {
      enabled: enabled && !!supplierId,
    },
  });
};

// Get a single product group
export const useProductGroup = (id: string, enabled = true) => {
  return useGetApi<ProductGroup>({
    endpoint: `rebates/product-groups/${id}`,
    options: {
      enabled: enabled && !!id,
    },
  });
};

// Create one or more product groups (comma-separated names create multiple)
export const useCreateProductGroup = () => {
  return useMutationApi<
    CreateProductGroupRequest,
    ProductGroup | ProductGroup[]
  >({
    endpoint: 'rebates/product-groups',
    method: 'POST',
    successMessage: 'Product group(s) created successfully',
  });
};

// Update a product group
export const useUpdateProductGroup = (id: string) => {
  return useMutationApi<UpdateProductGroupRequest, ProductGroup>({
    endpoint: `rebates/product-groups/${id}`,
    method: 'PUT',
    successMessage: 'Product group updated successfully',
  });
};

// Delete a product group
export const useDeleteProductGroup = (
  productGroupId: string,
  forceDeleteInvoices = false,
) => {
  const queryParams = forceDeleteInvoices ? '?forceDeleteInvoices=true' : '';
  return useMutationApi<Record<string, never>, DeleteProductGroupResponse>({
    endpoint: `rebates/product-groups/${productGroupId}${queryParams}`,
    method: 'DELETE',
    successMessage: 'Product group deleted successfully',
  });
};

// Get tags for a product group
export const useProductGroupTags = (productGroupId: string, enabled = true) => {
  return useGetApi<
    {
      id: string;
      coreEnumOptionId: string;
      tag: ProductGroupTag;
    }[]
  >({
    endpoint: `rebates/product-groups/${productGroupId}/tags`,
    options: {
      enabled: enabled && !!productGroupId,
    },
  });
};

// Add a tag to a product group
export const useAddProductGroupTag = (productGroupId: string) => {
  return useMutationApi<
    { tagId: string },
    { id: string; tag: ProductGroupTag }
  >({
    endpoint: `rebates/product-groups/${productGroupId}/tags`,
    method: 'POST',
    successMessage: 'Tag added successfully',
  });
};

// Remove a tag from a product group
export const useRemoveProductGroupTag = (
  productGroupId: string,
  tagId: string,
) => {
  return useMutationApi<Record<string, never>>({
    endpoint: `rebates/product-groups/${productGroupId}/tags/${tagId}`,
    method: 'DELETE',
    successMessage: 'Tag removed successfully',
  });
};
